/**
 * @module lib/supabase
 * @description Cliente Supabase singleton y utilidades de integración para el sistema ACSGC.
 *
 * Provee el punto de acceso único a PostgreSQL, Auth y Storage de Supabase.
 * Si las variables de entorno no están configuradas, la aplicación cae automáticamente
 * en modo demo (datos mock, sin conexión real).
 *
 * ## Exports principales
 * | Export | Tipo | Propósito |
 * |--------|------|-----------|
 * | `supabase` | SupabaseClient | Cliente singleton con tipos TypeScript |
 * | `getCurrentUser()` | función | Obtiene usuario autenticado desde PostgreSQL |
 * | `signIn()` | función | Login con email+password vía Supabase Auth |
 * | `signOut()` | función | Cierre de sesión |
 * | `executeQuery()` | función | Helper genérico para consultas con manejo de errores |
 * | `checkConnection()` | función | Verifica conectividad contra Supabase |
 * | `uploadFile()` | función | Sube archivos al Storage de Supabase |
 * | `uploadAvatar()` | función | Sube avatar del usuario al bucket 'avatars' |
 * | `deleteAvatar()` | función | Elimina avatar anterior del Storage |
 * | `subscribeToTable()` | función | Suscripción Realtime a cambios de tabla |
 * | `clearAuthStorage()` | función | Limpia tokens corruptos del localStorage |
 *
 * ## Configuración de Auth
 * - PKCE flow para mayor seguridad
 * - `persistSession: true` — la sesión sobrevive al refresco del navegador
 * - Clave de storage: `iso-compliance-auth-token`
 * - `autoRefreshToken: true` — el JWT se renueva automáticamente antes de expirar
 *
 * ## Modo demo
 * Si `VITE_SUPABASE_URL` o `VITE_SUPABASE_ANON_KEY` están vacíos, el cliente se crea
 * con placeholders. Las consultas fallarán silenciosamente y los contextos activarán
 * sus datos mock. Esto permite desarrollo sin cuenta Supabase activa.
 *
 * @see AuthContext para el flujo de autenticación completo
 * @see DataContext para cómo se usa el cliente en consultas ISO
 */

import { createClient } from '@supabase/supabase-js';
import type { Database } from './database.types';

// =====================================================
// CONFIGURACIÓN DE SUPABASE
// =====================================================

/**
 * URL del proyecto Supabase
 * Se obtiene de las variables de entorno
 */
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';

/**
 * Clave pública (anon key) de Supabase
 * Esta clave es segura para usar en el frontend
 */
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

// Validación de configuración (no lanza errores, solo advierte)
const isSupabaseConfigured = supabaseUrl && supabaseAnonKey;

if (!isSupabaseConfigured) {
  console.warn(
    '⚠️ [SUPABASE] Variables de entorno no configuradas. ' +
    'La aplicación funcionará en modo demo. ' +
    'Para habilitar Supabase, configura VITE_SUPABASE_URL y VITE_SUPABASE_ANON_KEY en tu archivo .env'
  );
}

// =====================================================
// CLIENTE SUPABASE
// =====================================================

/**
 * Cliente Supabase singleton con tipos TypeScript
 *
 * Configuración:
 * - Autenticación automática
 * - Persistencia de sesión
 * - Tipos TypeScript generados
 * - RLS (Row Level Security) habilitado
 * - Modo fallback si no hay configuración
 */
export const supabase = createClient<Database>(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder-anon-key',
  {
    auth: {
      // Configuración de autenticación
      // ✅ CRÍTICO: SIEMPRE habilitar persistencia para mantener sesión al refrescar
      autoRefreshToken: true,  // Cambio: siempre true, no depende de isSupabaseConfigured
      persistSession: true,     // Cambio: siempre true, no depende de isSupabaseConfigured
      detectSessionInUrl: true, // Cambio: siempre true, no depende de isSupabaseConfigured

      // Configuración de almacenamiento de sesión
      // ✅ CRÍTICO: SIEMPRE usar localStorage para persistir sesión
      storage: window.localStorage, // Cambio: siempre usar localStorage

      // Configuración de cookies (para SSR si se implementa)
      storageKey: 'iso-compliance-auth-token',

      // ✅ NUEVO: Usar PKCE flow para mejor seguridad
      flowType: 'pkce',

      // Configuración de redirección
      redirectTo: import.meta.env.VITE_AUTH_REDIRECT_URL || window.location.origin,
    },

    // Configuración global de la base de datos
    db: {
      schema: 'public',
    },

    // Configuración de tiempo de espera
    global: {
      headers: {
        'X-Client-Info': 'iso-compliance-system@1.0.0',
      },
    },
  }
);

// =====================================================
// TIPOS Y INTERFACES
// =====================================================

/**
 * Tipo para el usuario autenticado con información adicional
 */
export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'technical_referent' | 'auditor' | 'viewer';
  organization_id: string;
  unit: string;
  avatar_url?: string;
  is_active: boolean;
  metadata?: Record<string, any>;
  last_login_at?: string;
}

/**
 * Tipo para la sesión de usuario
 */
export interface UserSession {
  user: AuthUser;
  access_token: string;
  refresh_token: string;
  expires_at: number;
}

/**
 * Tipo para errores de Supabase
 */
export interface SupabaseError {
  message: string;
  details?: string;
  hint?: string;
  code?: string;
}

// =====================================================
// UTILIDADES DE AUTENTICACIÓN
// =====================================================

/**
 * Obtiene el usuario actual autenticado con información completa
 * 
 * @returns Promise<AuthUser | null> - Usuario autenticado o null
 */
export async function getCurrentUser(): Promise<AuthUser | null> {
  try {
    // Obtener sesión actual
    console.log('🔍 [SUPABASE] Obteniendo sesión actual...');
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();

    if (sessionError) {
      console.error('❌ [SUPABASE] Error obteniendo sesión:', sessionError);
      return null;
    }

    if (!session?.user) {
      console.warn('⚠️ [SUPABASE] No hay sesión activa');
      return null;
    }

    console.log('✅ [SUPABASE] Sesión encontrada, user ID:', session.user.id);

    // Obtener información completa del usuario desde la base de datos
    console.log('🔍 [SUPABASE] Consultando tabla users...');
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select(`
        id,
        organization_id,
        name,
        email,
        role,
        unit,
        avatar_url,
        metadata,
        is_active,
        last_login_at
      `)
      .eq('id', session.user.id)
      .eq('is_active', true)
      .single();

    if (userError) {
      console.error('❌ [SUPABASE] Error obteniendo datos del usuario:', userError);
      console.error('❌ [SUPABASE] Detalles del error:', {
        message: userError.message,
        details: userError.details,
        hint: userError.hint,
        code: userError.code
      });
      return null;
    }

    if (!userData) {
      console.warn('⚠️ [SUPABASE] Usuario no encontrado en la base de datos');
      return null;
    }

    console.log('✅ [SUPABASE] Datos del usuario obtenidos:', {
      id: userData.id,
      name: userData.name,
      email: userData.email,
      role: userData.role
    });

    return userData as AuthUser;

  } catch (error) {
    console.error('❌ [SUPABASE] Error inesperado obteniendo usuario:', error);
    return null;
  }
}

/**
 * Inicia sesión con email y contraseña
 * 
 * @param email - Email del usuario
 * @param password - Contraseña del usuario
 * @returns Promise<{ user: AuthUser | null, error: SupabaseError | null }>
 */
export async function signIn(email: string, password: string) {
  try {
    // Intentar iniciar sesión
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password: password,
    });
    
    if (error) {
      return { user: null, error: error as SupabaseError };
    }
    
    if (!data.user) {
      return { 
        user: null, 
        error: { message: 'No se pudo autenticar el usuario' } as SupabaseError 
      };
    }
    
    // Actualizar último login
    await supabase
      .from('users')
      .update({ last_login_at: new Date().toISOString() })
      .eq('id', data.user.id);
    
    // Obtener información completa del usuario
    const user = await getCurrentUser();
    
    return { user, error: null };
    
  } catch (error) {
    console.error('❌ Error inesperado en signIn:', error);
    return { 
      user: null, 
      error: { message: 'Error inesperado durante el login' } as SupabaseError 
    };
  }
}

/**
 * Cierra la sesión del usuario actual
 * 
 * @returns Promise<{ error: SupabaseError | null }>
 */
export async function signOut() {
  try {
    const { error } = await supabase.auth.signOut();
    
    if (error) {
      return { error: error as SupabaseError };
    }
    
    return { error: null };
    
  } catch (error) {
    console.error('❌ Error inesperado en signOut:', error);
    return { 
      error: { message: 'Error inesperado durante el logout' } as SupabaseError 
    };
  }
}

// =====================================================
// UTILIDADES DE BASE DE DATOS
// =====================================================

/**
 * Ejecuta una consulta con manejo de errores mejorado
 * 
 * @param queryFn - Función que ejecuta la consulta
 * @returns Promise con resultado y error manejado
 */
export async function executeQuery<T>(
  queryFn: () => Promise<{ data: T | null; error: any }>
): Promise<{ data: T | null; error: SupabaseError | null }> {
  try {
    const result = await queryFn();
    
    if (result.error) {
      console.error('❌ Error en consulta:', result.error);
      return {
        data: null,
        error: {
          message: result.error.message || 'Error en la consulta',
          details: result.error.details,
          hint: result.error.hint,
          code: result.error.code,
        } as SupabaseError,
      };
    }
    
    return { data: result.data, error: null };
    
  } catch (error) {
    console.error('❌ Error inesperado en consulta:', error);
    return {
      data: null,
      error: {
        message: 'Error inesperado en la consulta',
        details: error instanceof Error ? error.message : 'Error desconocido',
      } as SupabaseError,
    };
  }
}

/**
 * Verifica la conexión con Supabase
 * 
 * @returns Promise<boolean> - true si la conexión es exitosa
 */
export async function checkConnection(): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('organizations')
      .select('id')
      .limit(1);
    
    if (error) {
      console.error('❌ Error de conexión con Supabase:', error);
      return false;
    }
    
    console.log('✅ Conexión con Supabase exitosa');
    return true;
    
  } catch (error) {
    console.error('❌ Error inesperado verificando conexión:', error);
    return false;
  }
}

// =====================================================
// UTILIDADES DE STORAGE
// =====================================================

/**
 * Sube un archivo al storage de Supabase
 * 
 * @param bucket - Nombre del bucket
 * @param path - Ruta del archivo
 * @param file - Archivo a subir
 * @returns Promise con URL del archivo subido
 */
export async function uploadFile(
  bucket: string,
  path: string,
  file: File
): Promise<{ url: string | null; error: SupabaseError | null }> {
  try {
    // Validar tamaño del archivo
    const maxSize = parseInt(import.meta.env.VITE_MAX_FILE_SIZE || '10485760'); // 10MB por defecto
    if (file.size > maxSize) {
      return {
        url: null,
        error: {
          message: `El archivo es demasiado grande. Tamaño máximo: ${(maxSize / 1024 / 1024).toFixed(1)}MB`,
        } as SupabaseError,
      };
    }
    
    // Subir archivo
    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(path, file, {
        cacheControl: '3600',
        upsert: false,
      });
    
    if (error) {
      return { url: null, error: error as SupabaseError };
    }
    
    // Obtener URL pública
    const { data: { publicUrl } } = supabase.storage
      .from(bucket)
      .getPublicUrl(data.path);
    
    return { url: publicUrl, error: null };
    
  } catch (error) {
    console.error('❌ Error subiendo archivo:', error);
    return {
      url: null,
      error: {
        message: 'Error inesperado subiendo archivo',
        details: error instanceof Error ? error.message : 'Error desconocido',
      } as SupabaseError,
    };
  }
}

/**
 * Sube un avatar de usuario a Supabase Storage
 * @param userId - ID del usuario
 * @param file - Archivo de imagen
 * @returns Promise con URL del avatar subido
 */
export async function uploadAvatar(
  userId: string,
  file: File
): Promise<{ url: string | null; error: SupabaseError | null }> {
  try {
    // Verificar sesión autenticada
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.error('❌ [STORAGE] No hay sesión autenticada');
      return {
        url: null,
        error: {
          message: 'Debes estar autenticado para subir avatares',
        } as SupabaseError,
      };
    }

    console.log('✅ [STORAGE] Sesión autenticada:', session.user.id);

    // Validar que sea una imagen
    if (!file.type.startsWith('image/')) {
      return {
        url: null,
        error: {
          message: 'El archivo debe ser una imagen (JPG, PNG, GIF, WebP)',
        } as SupabaseError,
      };
    }

    // Validar tamaño (máximo 2MB para avatares)
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      return {
        url: null,
        error: {
          message: 'El avatar es demasiado grande. Tamaño máximo: 2MB',
        } as SupabaseError,
      };
    }

    // Generar nombre único para el archivo
    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}-${Date.now()}.${fileExt}`;
    // NO incluir "avatars/" porque ya estamos en el bucket 'avatars'
    const filePath = fileName;

    console.log('📤 [STORAGE] Subiendo avatar:', filePath);

    // Subir a bucket 'avatars'
    const { data, error } = await supabase.storage
      .from('avatars')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true, // Reemplazar si existe
      });

    if (error) {
      console.error('❌ [STORAGE] Error subiendo avatar:', {
        message: error.message,
        statusCode: error.statusCode,
        error: error
      });
      return { url: null, error: error as SupabaseError };
    }

    console.log('✅ [STORAGE] Avatar subido exitosamente:', data.path);

    // Obtener URL pública
    const { data: { publicUrl } } = supabase.storage
      .from('avatars')
      .getPublicUrl(data.path);

    console.log('🔗 [STORAGE] URL pública:', publicUrl);

    return { url: publicUrl, error: null };

  } catch (error) {
    console.error('❌ [STORAGE] Error inesperado subiendo avatar:', error);
    return {
      url: null,
      error: {
        message: 'Error inesperado subiendo avatar',
        details: error instanceof Error ? error.message : 'Error desconocido',
      } as SupabaseError,
    };
  }
}

/**
 * Elimina un avatar anterior del usuario
 * @param avatarUrl - URL del avatar a eliminar
 */
export async function deleteAvatar(avatarUrl: string): Promise<{ error: SupabaseError | null }> {
  try {
    // Extraer el path del avatar de la URL
    // Ejemplo URL: https://xxx.supabase.co/storage/v1/object/public/avatars/user-123.jpg
    const urlParts = avatarUrl.split('/avatars/');
    if (urlParts.length < 2) {
      return { error: null }; // No es un avatar de nuestro storage
    }

    // Solo el nombre del archivo, sin "avatars/" porque ya estamos en el bucket
    const filePath = urlParts[1];

    const { error } = await supabase.storage
      .from('avatars')
      .remove([filePath]);

    if (error) {
      console.warn('⚠️ Error eliminando avatar anterior:', error);
      // No fallar si no se puede eliminar el avatar anterior
      return { error: null };
    }

    return { error: null };

  } catch (error) {
    console.error('❌ Error eliminando avatar:', error);
    return { error: null }; // No fallar por esto
  }
}

// =====================================================
// UTILIDADES DE TIEMPO REAL
// =====================================================

/**
 * Suscribe a cambios en tiempo real de una tabla
 * 
 * @param table - Nombre de la tabla
 * @param callback - Función callback para cambios
 * @returns Función para cancelar la suscripción
 */
export function subscribeToTable(
  table: string,
  callback: (payload: any) => void
): () => void {
  const subscription = supabase
    .channel(`public:${table}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table },
      callback
    )
    .subscribe();
  
  // Retornar función para cancelar suscripción
  return () => {
    subscription.unsubscribe();
  };
}

// =====================================================
// UTILIDADES DE ALMACENAMIENTO
// =====================================================

/**
 * Limpia el localStorage de datos de autenticación corruptos
 * Útil cuando hay problemas de sesión persistentes
 */
export function clearAuthStorage(): void {
  try {
    const keysToRemove = [
      'iso-compliance-auth-token',
      'sb-access-token',
      'sb-refresh-token'
    ];

    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (err) {
        console.warn(`⚠️ [SUPABASE] No se pudo eliminar clave ${key}:`, err);
      }
    });

    console.log('✅ [SUPABASE] Almacenamiento de autenticación limpiado');
  } catch (error) {
    console.error('❌ [SUPABASE] Error limpiando almacenamiento:', error);
  }
}

/**
 * Valida y repara el estado del almacenamiento
 * Se ejecuta automáticamente al inicializar
 */
function validateAndRepairStorage(): void {
  try {
    // Verificar si localStorage está disponible
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);

    // Verificar si hay datos corruptos en las claves de autenticación
    const authKey = 'iso-compliance-auth-token';
    const authData = localStorage.getItem(authKey);

    if (authData) {
      try {
        JSON.parse(authData);
      } catch (parseError) {
        console.warn('⚠️ [SUPABASE] Datos de autenticación corruptos detectados, limpiando...');
        clearAuthStorage();
      }
    }
  } catch (error) {
    console.warn('⚠️ [SUPABASE] localStorage no disponible o con errores:', error);
  }
}

// Ejecutar validación al inicializar
validateAndRepairStorage();

// =====================================================
// CONFIGURACIÓN DE DESARROLLO
// =====================================================

// En modo desarrollo, agregar información de debugging
if (import.meta.env.VITE_DEBUG_MODE === 'true') {
  console.log('🔧 Modo debug habilitado para Supabase');
  console.log('📡 URL de Supabase:', supabaseUrl);
  console.log('🔑 Anon Key configurada:', !!supabaseAnonKey);

  // Verificar conexión al inicializar
  if (isSupabaseConfigured) {
    checkConnection().then(connected => {
      if (connected) {
        console.log('✅ Conexión inicial con Supabase exitosa');
      } else {
        console.error('❌ Fallo en conexión inicial con Supabase');
      }
    });
  }
}

// =====================================================
// EXPORTACIONES POR DEFECTO
// =====================================================

export default supabase;

// Exportar tipos para uso en otros archivos
export type { Database } from './database.types';